import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ErrorModel } from '@/shared/api/BaseAPI.model'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import EmploymentProfilesAPI from '../api/EmploymentProfilesAPI'
import {
  EMPLOYMENT_QUERY,
  EmploymentProfileDto
} from '../api/EmploymentProfilesAPI.model'
import { toProfilesByUserId, toUpsertDto } from '../api/employmentMapper'
import { EMPLOYMENT_USE_API } from '../config'
import { EmploymentProfile } from '../types/employment.model'

/**
 * Employment profiles as the backend holds them.
 *
 * Inert while `EMPLOYMENT_USE_API` is false: the query never runs and the map
 * stays empty, which is what lets `useEmploymentProfiles` fall back to the
 * local store without either source guessing at the other.
 */
export const useEmploymentProfilesQuery = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()

  const { data, isLoading, error } = useQuery({
    // Employment is a relationship with one venue, so the cache is keyed by it
    //, switching restaurants must not show the previous one's rates.
    queryKey: [
      EMPLOYMENT_QUERY.PROFILES,
      localStorage.getItem('restaurantId')
    ],
    queryFn: () => EmploymentProfilesAPI.getProfiles(),
    enabled: EMPLOYMENT_USE_API
  })

  const profilesByUserId = useMemo(
    () => toProfilesByUserId(data ?? []),
    [data]
  )

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [EMPLOYMENT_QUERY.PROFILES] })

  const showSaveError = () =>
    showSnackbar(
      t('ALERTS.SAVE_EMPLOYMENT_PROFILE_ERROR.TITLE'),
      t('ALERTS.SAVE_EMPLOYMENT_PROFILE_ERROR.DESCRIPTION'),
      'error'
    )

  /**
   * Takes the whole profile, not a patch: the PUT replaces the stored row, so
   * anything the caller leaves out is wiped rather than kept.
   */
  const { mutate: upsertProfile } = useMutation<
    EmploymentProfileDto,
    ErrorModel,
    EmploymentProfile
  >({
    mutationFn: profile =>
      EmploymentProfilesAPI.upsertProfile(profile.userId, toUpsertDto(profile)),
    onSuccess: invalidate,
    onError: showSaveError
  })

  const { mutate: deleteProfile } = useMutation<void, ErrorModel, string>({
    mutationFn: userId => EmploymentProfilesAPI.deleteProfile(userId),
    onSuccess: invalidate,
    // Clearing every field is still a save from the user's side, so a failure
    // reads the same way.
    onError: showSaveError
  })

  return {
    profilesByUserId,
    isLoading: EMPLOYMENT_USE_API && isLoading,
    // Told apart from an empty list on purpose: with no rates readable every
    // payroll row looks like an employee nobody set a rate for, which blames
    // the data for what is a permission on read:employment-profiles.
    isForbidden:
      (error as { response?: { status?: number } } | null)?.response?.status ===
      403,
    upsertProfile,
    deleteProfile
  }
}
